class VariantSpecificContent extends HTMLElement {
  connectedCallback() {
    if (this.isInitialized) return;

    this.isInitialized = true;
    this.blocks = Array.from(this.querySelectorAll('[data-variant-content-block]'));
    this.variants = this.readVariants();
    this.form = this.findMainProductForm();
    this.formVariants = this.readFormVariants(this.form);

    this.handleVariantEvent = this.handleVariantEvent.bind(this);
    this.handleFormChange = this.handleFormChange.bind(this);

    document.addEventListener('variant:change', this.handleVariantEvent);
    document.addEventListener('hsw5:variant-change', this.handleVariantEvent);
    this.form?.addEventListener('change', this.handleFormChange);

    this.updateFromForm();
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.handleVariantEvent);
    document.removeEventListener('hsw5:variant-change', this.handleVariantEvent);
    this.form?.removeEventListener('change', this.handleFormChange);
    this.isInitialized = false;
  }

  readVariants() {
    const data = this.querySelector('[data-variant-content-variants]');

    try {
      return JSON.parse(data?.textContent || '[]');
    } catch (error) {
      console.error('Variant-specific content data could not be parsed.', error);
      return [];
    }
  }

  findMainProductForm() {
    const pageRoot = document.querySelector('#MainContent') || document.querySelector('main') || document;
    const firstWatchHeroForm = pageRoot.querySelector(
      '.watch-product-hero[data-section-id] .watch-product-hero__product-form[data-product-form]'
    ) || document.querySelector(
      '.watch-product-hero[data-section-id] .watch-product-hero__product-form[data-product-form]'
    );
    if (firstWatchHeroForm) return firstWatchHeroForm;

    const productSections = Array.from(pageRoot.querySelectorAll('.shopify-section, [data-section-id]'));

    for (const section of productSections) {
      const form = section.querySelector(
        '.watch-product-hero__product-form[data-product-form], form[data-product-form][action*="/cart/add"]'
      );
      if (form) return form;
    }

    const firstPdpForm = pageRoot.querySelector(
      '.watch-product-hero__product-form[data-product-form], form[data-product-form][action*="/cart/add"]'
    );
    if (firstPdpForm) return firstPdpForm;

    const forms = Array.from(document.querySelectorAll('[data-product-form]'));
    const variantIds = new Set(this.variants.map((variant) => String(variant.id)));

    return forms.find((form) => {
      const input = form.querySelector('[name="id"]');
      return input && variantIds.has(String(input.value));
    }) || document.querySelector('main [data-product-form]') || forms[0] || null;
  }

  readFormVariants(form) {
    const productSection = form?.closest('.watch-product-hero, [data-section-id], .shopify-section');
    const data = productSection?.querySelector('[data-hsw5-ab-products]');
    if (!data) return [];

    try {
      const catalog = JSON.parse(data.textContent || '{}');
      const variants = [];

      Object.values(catalog).forEach((product) => {
        if (product && Array.isArray(product.variants)) variants.push(...product.variants);
      });

      return variants;
    } catch (error) {
      console.error('Product-form variant data could not be parsed.', error);
      return [];
    }
  }

  findVariant(variantId) {
    const source = this.formVariants.length ? this.formVariants : this.variants;
    return source.find((variant) => Number(variant.id) === Number(variantId));
  }

  handleVariantEvent(event) {
    const variant = event.detail?.variant;
    if (!variant || !this.findVariant(variant.id)) return;

    this.render(variant.options || []);
  }

  handleFormChange() {
    window.requestAnimationFrame(() => this.updateFromForm());
  }

  updateFromForm() {
    const variantInput = this.form?.querySelector('[name="id"]');
    const variant = this.findVariant(variantInput?.value);

    if (variant) this.render(variant.options);
  }

  render(options) {
    const normalizedOptions = options.map((option) => this.normalize(option));
    let visibleBlocks = 0;

    this.blocks.forEach((block) => {
      const requiredOption = this.normalize(block.dataset.variantOption);
      const isVisible = !requiredOption || normalizedOptions.includes(requiredOption);

      block.hidden = !isVisible;
      this.updateBlockVideos(block, isVisible);
      if (isVisible) visibleBlocks += 1;
    });

    this.dataset.visibleBlocks = String(visibleBlocks);
    this.hidden = visibleBlocks === 0;
  }

  updateBlockVideos(block, isVisible) {
    const shouldAutoplay = isVisible && block.dataset.mediaAutoplay === 'true';

    block.querySelectorAll('video').forEach((video) => {
      if (!isVisible) {
        video.pause();
        return;
      }

      video.preload = 'metadata';
      if (shouldAutoplay) video.play().catch(() => {});
    });
  }

  normalize(value) {
    return String(value || '').trim().toLocaleLowerCase();
  }
}

if (!customElements.get('variant-specific-content')) {
  customElements.define('variant-specific-content', VariantSpecificContent);
}
